import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { ChevronLeft, ChevronRight, AlertOctagon, CheckCircle2, Clock3, MessageSquareText } from "lucide-react";
import { StreakBadge, useStandupStats } from "./StandupStreaks";

const shift = (iso, days) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const pretty = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

export const shiftDay = (iso, days) => shift(iso, days);
export const prettyDay = (iso) => pretty(iso);

export default function StandupPanel({ projectId, currentUser, canPost }) {
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null);

  const load = async (d) => {
    try { const { data } = await api.get(`/projects/${projectId}/standups`, { params: d ? { date: d } : {} }); setData(data); setDate(data.date); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [projectId]);
  if (!data) return <div className="text-sm text-neutral-500">Loading standups…</div>;

  const isToday = date === data.today;
  const mine = data.notes.find(n => n.user_id === currentUser?.id);
  return (
    <div className="space-y-4" data-testid="standup-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Daily Standup</div>
          <h3 className="text-lg font-bold" data-testid="standup-date">{isToday ? "Today" : pretty(date)} · {data.notes.length} posted</h3>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => load(shift(date, -1))} data-testid="standup-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={isToday} data-testid="standup-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => load(shift(date, 1))} disabled={isToday} data-testid="standup-next-day"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <StandupSummary data={data} />
      {canPost && isToday && <MyStandupForm projectId={projectId} mine={mine} meId={currentUser?.id} onSaved={() => load(date)} />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.notes.map(n => <StandupCard key={n.id} n={n} />)}
      </div>
      {data.notes.length === 0 && <div className="panel p-8 text-center text-sm text-neutral-500" data-testid="standup-empty"><MessageSquareText className="w-6 h-6 mx-auto mb-2 opacity-50" />No standup notes for this day.</div>}
    </div>
  );
}

function StandupSummary({ data }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`px-2.5 py-1 rounded-full border font-mono ${data.blockers ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`} data-testid="standup-blocker-count">
        {data.blockers ? `${data.blockers} blocked` : "No blockers"}
      </span>
      {data.missing.length > 0 && <span className="text-neutral-500 font-mono">Not posted:</span>}
      {data.missing.map(u => (
        <span key={u.id} className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-600" data-testid={`standup-missing-${u.id}`}>{u.name}</span>
      ))}
    </div>
  );
}

export function StandupCard({ n }) {
  const blocked = !!n.blockers?.trim();
  const row = (Icon, label, text, cls) => text?.trim() ? (
    <div>
      <div className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${cls}`}><Icon className="w-3 h-3" />{label}</div>
      <div className="text-sm text-neutral-800 whitespace-pre-wrap mt-0.5">{text}</div>
    </div>
  ) : null;
  return (
    <div className={`panel p-4 space-y-3 ${blocked ? "border-rose-300 ring-1 ring-rose-200" : ""}`} data-testid={`standup-card-${n.user_id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">{n.user_name}</div>
        <div className="text-[10px] font-mono uppercase text-neutral-500">{(n.user_role || "").replace(/_/g, " ")} · {new Date(n.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
      {row(CheckCircle2, "Yesterday", n.yesterday, "text-neutral-500")}
      {row(Clock3, "Today", n.today, "text-blue-600")}
      {row(AlertOctagon, "Blockers", n.blockers, "text-rose-600")}
    </div>
  );
}

function MyStandupForm({ projectId, mine, meId, onSaved }) {
  const [form, setForm] = useState({ yesterday: "", today: "", blockers: "" });
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const stats = useStandupStats([savedAt]);
  useEffect(() => { setForm({ yesterday: mine?.yesterday || "", today: mine?.today || "", blockers: mine?.blockers || "" }); }, [mine?.updated_at]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/projects/${projectId}/standups/me`, form); toast.success(mine ? "Standup updated" : "Standup posted"); setSavedAt(Date.now()); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const box = (key, label, ph) => (
    <div className="space-y-1">
      <div className={`text-[10px] font-mono uppercase tracking-wider ${key === "blockers" ? "text-rose-600" : "text-neutral-500"}`}>{label}</div>
      <Textarea rows={3} value={form[key]} placeholder={ph} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} data-testid={`standup-field-${key}`} className="bg-white border-neutral-200 text-sm" />
    </div>
  );
  return (
    <div className="panel p-4 space-y-3" data-testid="standup-my-form">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold">{mine ? "Your standup (edit anytime today)" : "Post your standup"}</div>
        <StreakBadge s={stats?.find(s => s.user_id === meId)} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {box("yesterday", "Yesterday", "What did you finish?")}
        {box("today", "Today", "What will you work on?")}
        {box("blockers", "Blockers", "Anything stopping you? Leave blank if none")}
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="standup-save">{busy ? "Saving..." : mine ? "Update" : "Post standup"}</Button>
      </div>
    </div>
  );
}
